import React, { Component } from 'react';
import _extends from '@babel/runtime/helpers/esm/extends';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import PropTypes from 'prop-types';
import defaultJss, { createGenerateId, SheetsRegistry, create, getDynamicStyles, SheetsManager } from 'jss';
export { SheetsRegistry, createGenerateId } from 'jss';
import preset from 'jss-preset-default';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ThemeContext } from 'theming';
export { ThemeProvider, withTheme, createTheming } from 'theming';
import warning from 'tiny-warning';

var JssContext = React.createContext({
  sheetOptions: {},
  disableStylesGeneration: false
});

var memoize = function memoize(fn) {
  var lastArgs;
  var lastResult;
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (Array.isArray(lastArgs) && args.length === lastArgs.length) {
      var isSame = true;

      for (var i = 0; i < args.length; i++) {
        if (args[i] !== lastArgs[i]) {
          isSame = false;
        }
      }

      if (isSame) {
        return lastResult;
      }
    }

    lastArgs = args;
    lastResult = fn.apply(void 0, args);
    return lastResult;
  };
};

/* eslint-disable react/require-default-props, react/no-unused-prop-types */

var JssProvider =
/*#__PURE__*/
function (_Component) {
  _inheritsLoose(JssProvider, _Component);

  function JssProvider() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.managers = {};
    _this.createContext = memoize(function (outerContext, props) {
      var registry = props.registry,
          classNamePrefix = props.classNamePrefix,
          jss = props.jss,
          generateId = props.generateId,
          disableStylesGeneration = props.disableStylesGeneration,
          media = props.media; // Clone the outer context

      var context = _extends({}, outerContext);

      if (registry) {
        context.registry = registry; // This way we identify a new request on the server, because user will create
        // a new Registry instance for each.

        if (registry !== _this.registry) {
          // We reset managers because we have to regenerate all sheets for the new request.
          _this.managers = {};
          _this.registry = registry;
        }
      }

      context.managers = _this.managers;

      if (generateId) {
        context.sheetOptions.generateId = generateId;
      } else if (!context.sheetOptions.generateId) {
        if (!_this.generateId) {
          _this.generateId = createGenerateId();
        }

        context.sheetOptions.generateId = _this.generateId;
      } // Merge the classname prefix


      if (classNamePrefix) {
        context.sheetOptions.classNamePrefix = (context.sheetOptions.classNamePrefix || '') + classNamePrefix;
      }

      if (media !== undefined) {
        context.sheetOptions.media = media;
      }

      if (jss) {
        context.jss = jss;
      }

      if (disableStylesGeneration !== undefined) {
        context.disableStylesGeneration = disableStylesGeneration;
      }

      return context;
    });
    _this.generateId = void 0;
    _this.registry = void 0;

    _this.renderProvider = function (outerContext) {
      var children = _this.props.children; // $FlowFixMe

      var context = _this.createContext(outerContext, _this.props);

      return React.createElement(JssContext.Provider, {
        value: context
      }, children);
    };

    return _this;
  }

  var _proto = JssProvider.prototype;

  _proto.render = function render() {
    return React.createElement(JssContext.Consumer, null, this.renderProvider);
  };

  return JssProvider;
}(Component);

JssProvider.propTypes = {
  registry: PropTypes.instanceOf(SheetsRegistry),
  jss: PropTypes.instanceOf(defaultJss.constructor),
  generateId: PropTypes.func,
  classNamePrefix: PropTypes.string,
  disableStylesGeneration: PropTypes.bool,
  children: PropTypes.node.isRequired,
  media: PropTypes.string
};

var defaultJss$1 = create(preset());

var getDisplayName = (function (Component$$1) {
  return Component$$1.displayName || Component$$1.name || 'Component';
});

function mergeClasses(baseClasses, additionalClasses) {
  var combinedClasses = _extends({}, baseClasses);

  for (var name in additionalClasses) {
    combinedClasses[name] = name in combinedClasses ? combinedClasses[name] + " " + additionalClasses[name] : additionalClasses[name];
  }

  return combinedClasses;
}

var dynamicStylesNS = Symbol.for('dynamicStyles');

/**
 * Global index counter to preserve source order.
 * As we create the style sheet during componentWillMount lifecycle,
 * children are handled after the parents, so the order of style elements would
 * be parent->child. It is a problem though when a parent passes a className
 * which needs to override any childs styles. StyleSheet of the child has a higher
 * specificity, because of the source order.
 * So our solution is to render sheets them in the reverse order child->sheet, so
 * that parent has a higher specificity.
 */
var indexCounter = -100000;
var managersCounter = 0;

var NoRenderer = function NoRenderer(props) {
  return props.children || null;
};

var getStyles = function getStyles(styles, theme, displayName) {
  if (typeof styles !== 'function') {
    return styles;
  }

  process.env.NODE_ENV !== "production" ? warning(styles.length !== 0, "[JSS] <" + displayName + " />'s styles function doesn't rely on the \"theme\" argument. We recommend declaring styles as an object instead.") : void 0;
  return styles(theme);
};
/**
 * HOC creator function that wrapps the user component.
 *
 * `withStyles(styles, [options])(Component)`
 */


function withStyles(styles, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$index = _options.index,
      index = _options$index === void 0 ? indexCounter++ : _options$index,
      theming = _options.theming,
      injectTheme = _options.injectTheme,
      optionsJss = _options.jss,
      sheetOptions = _objectWithoutPropertiesLoose(_options, ["index", "theming", "injectTheme", "jss"]);

  var isThemingEnabled = typeof styles === 'function';
  var ThemeConsumer = theming && theming.context.Consumer || ThemeContext.Consumer;
  return function (InnerComponent) {
    if (InnerComponent === void 0) {
      InnerComponent = NoRenderer;
    }

    var displayName = getDisplayName(InnerComponent);
    var defaultClassNamePrefix = process.env.NODE_ENV === 'production' ? '' : displayName + "-";
    var managerId = managersCounter++;
    var manager = new SheetsManager();
    var noTheme = {}; // $FlowFixMe

    var getTheme = function getTheme(props) {
      return isThemingEnabled && props.theme ? props.theme : noTheme;
    };

    var WithStyles =
    /*#__PURE__*/
    function (_Component) {
      _inheritsLoose(WithStyles, _Component);

      // $FlowFixMe
      function WithStyles(props) {
        var _this;

        _this = _Component.call(this, props) || this;
        _this.mergeClassesProp = memoize(function (sheetClasses, classesProp) {
          return classesProp ? mergeClasses(sheetClasses, classesProp) : sheetClasses;
        });
        _this.classNamePrefix = void 0;
        var contextSheetOptions = props.jssContext.sheetOptions;
        _this.classNamePrefix = (contextSheetOptions.classNamePrefix || '') + defaultClassNamePrefix;
        _this.state = _this.createState();

        _this.manage(props, _this.state);

        return _this;
      }

      var _proto = WithStyles.prototype;

      _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
        var dynamicSheet = this.state.dynamicSheet;
        if (dynamicSheet) dynamicSheet.update(this.props);

        if (isThemingEnabled && this.props.theme !== prevProps.theme) {
          var newState = this.createState();
          this.manage(this.props, newState);
          this.unmanage(prevProps, prevState); // eslint-disable-next-line react/no-did-update-set-state

          this.setState(newState);
        }
      };

      _proto.componentWillUnmount = function componentWillUnmount() {
        this.unmanage(this.props, this.state);
      };

      _proto.getStaticSheet = function getStaticSheet() {
        var theme = getTheme(this.props);
        var staticSheet = this.manager.get(theme);

        if (staticSheet) {
          return staticSheet;
        }

        var themedStyles = getStyles(styles, theme, displayName);
        var contextSheetOptions = this.props.jssContext.sheetOptions;
        staticSheet = this.jss.createStyleSheet(themedStyles, _extends({}, sheetOptions, contextSheetOptions, {
          index: index,
          meta: displayName + ", " + (isThemingEnabled ? 'Themed' : 'Unthemed') + ", Static",
          classNamePrefix: this.classNamePrefix
        }));
        this.manager.add(theme, staticSheet); // $FlowFixMe Cannot add random fields to instance of class StyleSheet

        staticSheet[dynamicStylesNS] = getDynamicStyles(themedStyles);
        return staticSheet;
      };

      _proto.getDynamicSheet = function getDynamicSheet(staticSheet) {
        // $FlowFixMe Cannot access random fields on instance of class StyleSheet
        var dynamicStyles = staticSheet[dynamicStylesNS];
        if (!dynamicStyles) return undefined;
        var contextSheetOptions = this.props.jssContext.sheetOptions;
        return this.jss.createStyleSheet(dynamicStyles, _extends({}, sheetOptions, contextSheetOptions, {
          index: index,
          meta: displayName + ", " + (isThemingEnabled ? 'Themed' : 'Unthemed') + ", Dynamic",
          classNamePrefix: this.classNamePrefix,
          link: true
        }));
      };

      _proto.manage = function manage(props, state) {
        var dynamicSheet = state.dynamicSheet,
            staticSheet = state.staticSheet;
        var registry = props.jssContext.registry;

        if (!staticSheet) {
          return;
        }

        this.manager.manage(getTheme(props));

        if (registry) {
          registry.add(staticSheet);
        }

        if (dynamicSheet) {
          dynamicSheet.update(props).attach();

          if (registry) {
            registry.add(dynamicSheet);
          }
        }
      };

      _proto.unmanage = function unmanage(props, state) {
        this.manager.unmanage(getTheme(props));

        if (state.dynamicSheet) {
          this.jss.removeStyleSheet(state.dynamicSheet);
        }
      };

      _proto.createState = function createState() {
        if (this.props.jssContext.disableStylesGeneration) {
          return {
            classes: {}
          };
        }

        var staticSheet = this.getStaticSheet();
        var dynamicSheet = this.getDynamicSheet(staticSheet);
        return {
          staticSheet: staticSheet,
          dynamicSheet: dynamicSheet,
          classes: dynamicSheet ? mergeClasses(staticSheet.classes, dynamicSheet.classes) : staticSheet.classes
        };
      };

      _proto.render = function render() {
        var _this$props = this.props,
            innerRef = _this$props.innerRef,
            jssContext = _this$props.jssContext,
            theme = _this$props.theme,
            classes = _this$props.classes,
            rest = _objectWithoutPropertiesLoose(_this$props, ["innerRef", "jssContext", "theme", "classes"]);

        var sheetClasses = this.state.classes;

        var props = _extends({}, rest, {
          classes: this.mergeClassesProp(sheetClasses, classes)
        });

        if (innerRef) props.ref = innerRef;
        if (injectTheme) props.theme = theme;
        return React.createElement(InnerComponent, props);
      };

      _createClass(WithStyles, [{
        key: "jss",
        get: function get() {
          return this.props.jssContext.jss || optionsJss || defaultJss$1;
        }
      }, {
        key: "manager",
        get: function get() {
          var managers = this.props.jssContext.managers; // If `managers` map is present in the context, we use it in order to
          // let JssProvider reset them when new response has to render server-side.

          if (managers) {
            if (!managers[managerId]) {
              managers[managerId] = new SheetsManager();
            }

            return managers[managerId];
          }

          return manager;
        }
      }]);

      return WithStyles;
    }(Component); // $FlowFixMe: Sadly there is no support for forwardRef yet


    WithStyles.displayName = "WithStyles(" + displayName + ")";
    WithStyles.defaultProps = _extends({}, InnerComponent.defaultProps);
    var JssContextSubscriber = React.forwardRef(function (props, ref) {
      return React.createElement(JssContext.Consumer, null, function (context) {
        if (isThemingEnabled || injectTheme) {
          return React.createElement(ThemeConsumer, null, function (theme) {
            return React.createElement(WithStyles, _extends({
              innerRef: ref,
              theme: theme
            }, props, {
              jssContext: context
            }));
          });
        }

        return React.createElement(WithStyles, _extends({
          innerRef: ref
        }, props, {
          jssContext: context
        }));
      });
    });
    JssContextSubscriber.displayName = 'JssContextSubscriber';
    JssContextSubscriber.InnerComponent = InnerComponent;
    return hoistNonReactStatics(JssContextSubscriber, InnerComponent);
  };
}

export default withStyles;
export { JssProvider, defaultJss$1 as jss, JssContext };
